/**
 * Call list spreadsheet helpers (client side, SheetJS).
 *
 * Parsing reuses the Budget import (`parseSpreadsheetFile`, `NOT_MAPPED`); the header alias auto-mapping follows
 * the same idiom (exact alias match first, then "contains"), with the call centre's fields: first name, surname,
 * full name, phone, email and note. The server normalises phones, finds duplicates and matches CCMS members;
 * here we only shape the rows (plus the original cells for the review screen) and warn about obvious gaps.
 */

import * as XLSX from 'xlsx';
import type { CallCentreImportRowInput } from '@actschurch/ccms-sdk';
import { parseSpreadsheetFile, NOT_MAPPED } from '../../BudgetManagement/components/budgetImport';

export { parseSpreadsheetFile, NOT_MAPPED };

export type CallListField = 'firstName' | 'lastName' | 'fullName' | 'phone' | 'email' | 'note';

/** Field to sheet column index (as a string) or NOT_MAPPED. */
export type CallListMapping = Record<CallListField, string>;

export const EMPTY_CALL_LIST_MAPPING: CallListMapping = {
  firstName: NOT_MAPPED,
  lastName: NOT_MAPPED,
  fullName: NOT_MAPPED,
  phone: NOT_MAPPED,
  email: NOT_MAPPED,
  note: NOT_MAPPED,
};

export interface CallListFieldDef {
  field: CallListField;
  label: string;
  hint: string;
  /** Normalised header names that map to this field. */
  aliases: string[];
}

export const CALL_LIST_FIELDS: CallListFieldDef[] = [
  { field: 'firstName', label: 'First name', hint: 'Or map Full name instead', aliases: ['first name', 'firstname', 'first', 'given name', 'given names', 'forename', 'forenames', 'name first', 'voornaam'] },
  { field: 'lastName', label: 'Surname', hint: 'Optional', aliases: ['surname', 'last name', 'lastname', 'last', 'family name', 'van'] },
  { field: 'fullName', label: 'Full name', hint: 'When first name and surname are in one column', aliases: ['full name', 'fullname', 'name', 'names', 'person', 'contact name', 'contact', 'naam'] },
  { field: 'phone', label: 'Phone', hint: 'Needed to call them', aliases: ['phone', 'cell', 'cellphone', 'cell phone', 'cell number', 'cellphone number', 'mobile', 'mobile number', 'phone number', 'contact number', 'tel', 'telephone', 'number', 'whatsapp', 'selfoon'] },
  { field: 'email', label: 'Email', hint: 'Optional, helps matching', aliases: ['email', 'e mail', 'email address', 'e mail address', 'mail'] },
  { field: 'note', label: 'Note', hint: 'Optional, shown to the caller', aliases: ['note', 'notes', 'comment', 'comments', 'remarks', 'details', 'reason'] },
];

const normalise = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[_\-.]+/g, ' ')
    .replace(/[^\p{L}\p{N} ]+/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

/** True when the row looks like a header (any cell matches a known alias). */
export function looksLikeCallListHeader(row: string[] | undefined): boolean {
  if (!row) return false;
  return row.some((cell) => {
    const n = normalise(String(cell || ''));
    return !!n && CALL_LIST_FIELDS.some((f) => f.aliases.includes(n));
  });
}

/** Aliases too general for a "contains" match ("ID number" is not a phone, "Campus name" is not a name). */
const LOOSE_SKIP = new Set(['number', 'name', 'names', 'first', 'last', 'mail', 'contact', 'person', 'details', 'reason']);

/** Auto-map columns from a header row (exact alias match first, then contains). */
export function guessCallListMapping(headerRow: string[]): CallListMapping {
  const mapping: CallListMapping = { ...EMPTY_CALL_LIST_MAPPING };
  const taken = new Set<number>();
  const headers = headerRow.map((h) => normalise(String(h || '')));

  for (const def of CALL_LIST_FIELDS) {
    const idx = headers.findIndex((h, i) => !taken.has(i) && !!h && def.aliases.includes(h));
    if (idx >= 0) {
      mapping[def.field] = String(idx);
      taken.add(idx);
    }
  }
  for (const def of CALL_LIST_FIELDS) {
    if (mapping[def.field] !== NOT_MAPPED) continue;
    // "name" is inside "first name" and "surname": only look for a full name column when the parts are not mapped.
    if (def.field === 'fullName' && (mapping.firstName !== NOT_MAPPED || mapping.lastName !== NOT_MAPPED)) continue;
    const loose = def.aliases.filter((a) => a.length > 3 && !LOOSE_SKIP.has(a));
    const idx = headers.findIndex((h, i) => !taken.has(i) && !!h && loose.some((a) => h.includes(a)));
    if (idx >= 0) {
      mapping[def.field] = String(idx);
      taken.add(idx);
    }
  }
  return mapping;
}

/** One preview row: the SDK row plus warnings. */
export interface ParsedCallListRow {
  row: CallCentreImportRowInput;
  missingPhone: boolean;
  missingName: boolean;
}

/**
 * Map data rows (header already stripped). `firstRowNumber` is the sheet row number of the first data row
 * (2 with a header row). Rows that are empty in every column are skipped. `headers` names the original cells.
 */
export function buildCallListRows(dataRows: string[][], mapping: CallListMapping, firstRowNumber: number, headers: string[]): ParsedCallListRow[] {
  const pick = (r: string[], field: CallListField): string => {
    const col = mapping[field];
    if (col === NOT_MAPPED) return '';
    return String(r[Number(col)] ?? '').trim();
  };
  const out: ParsedCallListRow[] = [];
  dataRows.forEach((r, i) => {
    if (!r.some((c) => String(c ?? '').trim())) return;
    const firstName = pick(r, 'firstName');
    const lastName = pick(r, 'lastName');
    const fullName = pick(r, 'fullName');
    const phone = pick(r, 'phone');
    const email = pick(r, 'email');
    const note = pick(r, 'note');
    const raw: Record<string, string | null> = {};
    r.forEach((cell, ci) => {
      const v = String(cell ?? '').trim();
      if (!v) return;
      const key = (headers[ci] || `Column ${ci + 1}`).trim() || `Column ${ci + 1}`;
      raw[raw[key] !== undefined ? `${key} (${ci + 1})` : key] = v;
    });
    out.push({
      missingPhone: !phone,
      missingName: !firstName && !fullName,
      row: {
        rowNumber: firstRowNumber + i,
        firstName: firstName || null,
        lastName: lastName || null,
        fullName: fullName || null,
        phone: phone || null,
        email: email || null,
        note: note || null,
        raw,
      },
    });
  });
  return out;
}

/** A template whose headers map by themselves. */
export function downloadCallListTemplate(ext: 'csv' | 'xlsx' = 'csv'): void {
  const headers = ['First name', 'Surname', 'Phone', 'Email', 'Note'];
  const rows = [
    ['Thandi', 'Mokoena', '082 123 4567', 'thandi@example.com', 'Visited on Sunday'],
    ['Pieter', 'van der Merwe', '+27 83 765 4321', '', 'Filled in a connect card'],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'People');
  XLSX.writeFile(wb, `call-list-template.${ext}`, { bookType: ext });
}

/** Original cell keys arrive camel-cased from the API ("First Name" becomes "first Name"): show them readably. */
export function rawKeyLabel(key: string): string {
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : key;
}

/** Look a raw cell up by header, ignoring case and spacing (the API camel-cases the keys). */
export function rawCell(raw: Record<string, string | null> | null | undefined, header: string): string | null {
  if (!raw) return null;
  const want = header.replace(/\s+/g, '').toLowerCase();
  const key = Object.keys(raw).find((k) => k.replace(/\s+/g, '').toLowerCase() === want);
  return key ? raw[key] ?? null : null;
}
